import {
    isEmpty,
    any,
    filter,
    getOr,
    compose,
    get,
    negate,
    map,
    compact,
    propertyOf,
    sortBy,
    sum,
    minBy,
    gt,
    values,
    includes,
    pick,
    spread,
    over,
} from 'lodash/fp';
import { createSelector } from 'reselect';
import { createAsyncMetaSelectors } from '@mews/framework';
import { getStringOrUndefinedProp, getStringProp } from '@mews/fp';
import { getEnterpriseName } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getBookableServices } from '@mews-commander/legacy-entities/src/ducks/services';
import { META_STATE_STARTED } from '@mews-commander/legacy-entities/src/constants';
import { moduleSelector } from '../module';
import { createDateRangeKey } from '../utils/keys';
import { getResourceCategoryIdsOfSelectedResourceCategoryType } from './entities';
import { getOrder, getDateRangeKeyFromOrder } from './order';
import { getServiceId } from './screens';
import { getReservation } from './reservations';
import { getSelectedEntityTimeUnitServiceIds } from './services';

export const getAvailability = createSelector(
    moduleSelector,
    getStringProp('scope'),
    (state, scope) => state.availability[scope] || {}
);

export const getServiceAvailability = createSelector(
    getAvailability,
    getStringProp('serviceId'),
    (availability, serviceId) => availability[serviceId] || {}
);

export const isSelectedEntityAvailabilityFetching = createSelector(
    getSelectedEntityTimeUnitServiceIds,
    getAvailability,
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    (serviceIds, availability) => any((id) => getOr(false, [id, 'meta', 'isFetching'], availability))(serviceIds)
);
export const hasSelectedEntityAvailabilityFailed = createSelector(
    getSelectedEntityTimeUnitServiceIds,
    getAvailability,
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    (serviceIds, availability) => any((id) => getOr(false, [id, 'meta', 'hasFailed'], availability))(serviceIds)
);

export const hasSelectedEntityAvailabilityLoaded = createSelector(
    getSelectedEntityTimeUnitServiceIds,
    getAvailability,
    (serviceIds, availability) =>
        compose(
            negate(isEmpty),
            compact,
            // @ts-expect-error $TSFixMeError: No overload matches this call.
            map((id) => get([id, 'categories'], availability))
        )(serviceIds)
);

export const getSelectedServiceAvailability = createSelector(
    getAvailability,
    getServiceId,
    getStringOrUndefinedProp('serviceId'),
    (availability, selectedServiceId, propsServiceId) => availability[propsServiceId || selectedServiceId] || {}
);

const getSelectedServiceAvailabilityMeta = createSelector(
    getSelectedServiceAvailability,
    (a) => a.meta || META_STATE_STARTED.meta
);

export const [isSelectedServiceAvailabilityFetching, hasSelectedServiceAvailabilityFailed] = createAsyncMetaSelectors(
    getSelectedServiceAvailabilityMeta
);

const getServiceAvailableCategories = (getSomeServiceAvailability: $TSFixMe) =>
    createSelector(getSomeServiceAvailability, (a) => (a as $TSFixMe).categories || {});

const getRemainingCount = (getCategoryAvailability: $TSFixMe) =>
    createSelector(getCategoryAvailability, get('remainingCount'));

export const [getAnyServiceAvailableCategories, getSelectedServiceAvailableCategories] = map(
    getServiceAvailableCategories
)([getServiceAvailability, getSelectedServiceAvailability]);

export const hasServiceAvailableCategories = compose(
    ([categories, dateRangeKey]) => !isEmpty(categories[dateRangeKey]),
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    over([getServiceAvailableCategories(getServiceAvailability), getDateRangeKeyFromOrder])
);

const getServiceCategoryAvailabilityByReservationDate = (getAvailableCategories: $TSFixMe) =>
    createSelector(
        getReservation,
        getStringProp('categoryId'),
        getAvailableCategories,
        (reservation, categoryId, categories) => get([createDateRangeKey(reservation), categoryId], categories)
    );

export const [
    getAnyServiceCategoryAvailabilityByReservationDate,
    getSelectedServiceCategoryAvailabilityByReservationDate,
] = map(getServiceCategoryAvailabilityByReservationDate)([
    getAnyServiceAvailableCategories,
    getSelectedServiceAvailableCategories,
]);

export const [
    getAnyServiceCategoryAvailabilityByReservationDateRemainingCount,
    getSelectedServiceCategoryAvailabilityByReservationDateRemainingCount,
] = map(getRemainingCount)([
    getAnyServiceCategoryAvailabilityByReservationDate,
    getSelectedServiceCategoryAvailabilityByReservationDate,
]);

const getServiceCategoryAvailabilityByOrderDate = (getAvailableCategories: $TSFixMe) =>
    createSelector(getOrder, getStringProp('categoryId'), getAvailableCategories, (order, categoryId, categories) =>
        get([createDateRangeKey(order), categoryId], categories)
    );

export const [getAnyServiceCategoryAvailability, getSelectedServiceCategoryAvailability] = map(
    getServiceCategoryAvailabilityByOrderDate
)([getAnyServiceAvailableCategories, getSelectedServiceAvailableCategories]);

export const [getAnyServiceCategoryAvailabilityRemainingCount, getSelectedServiceCategoryAvailabilityRemainingCount] =
    map(getRemainingCount)([getAnyServiceCategoryAvailability, getSelectedServiceCategoryAvailability]);

export const getAnyServiceCategoryAvailabilityPrice = createSelector(getAnyServiceCategoryAvailability, get('price'));

const getAvailabilityMatcher = createSelector(
    getAvailability,
    getStringProp('serviceId'),
    getOrder,
    (availability, serviceId, order) =>
        compose(propertyOf, get([serviceId, 'categories', createDateRangeKey(order)]))(availability)
);

export const getTotalAvailability = createSelector(
    getResourceCategoryIdsOfSelectedResourceCategoryType,
    getAvailabilityMatcher,
    (categoryIds, availabilityMatcher) => compose(sum, map('remainingCount'), map(availabilityMatcher))(categoryIds)
);

const getMinCategory = createSelector(
    getResourceCategoryIdsOfSelectedResourceCategoryType,
    getAvailabilityMatcher,
    (categoryIds, availabilityMatcher) => compose(minBy(get(['price', 'value'])), map(availabilityMatcher))(categoryIds)
);

export const getEnterpriseAvailabilityInfo = createSelector(
    getEnterpriseName,
    getTotalAvailability,
    getMinCategory,
    getStringProp('serviceId'),
    (name, totalAvailability, minCategory, serviceId) => {
        const fromPrice = get('price', minCategory);

        return {
            name,
            totalAvailability,
            fromPrice,
            serviceId,
        };
    }
);

export const hasOverbooking = createSelector(
    getSelectedServiceCategoryAvailability,
    compose(spread(gt), values, pick(['selectedCount', 'remainingCount']))
);

export const getSelectedEntityFilteredEnterpriseServices = createSelector(
    getSelectedEntityTimeUnitServiceIds,
    getBookableServices,
    (serviceIds, services) =>
        compose(
            sortBy(['enterpriseId', 'ordering', 'createdUtc']),
            filter(({ id }) => includes(id)(serviceIds))
        )(services)
);
